import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { AuthenticationApiService } from "../user";
import { handleTokenStore, decodeAndStoreToken } from "./helpers";
import { ACCESS, REFRESH } from "@zenith/data/constants";
import { AppNavigation } from "@zenith/hooks";
import { InactivityMethods } from "@zenith/features/Authentication/utils/inactivityModal";
import { Outlet } from "react-router-dom";
import {
  useGlobalContextActions,
  useUserDetailsContext,
} from "@zenith/context/global-context/hooks";
import { SIGN_IN } from "@zenith/routes/paths";
import { generateHexId, generateTraceHeaders } from "@zenith/utils/tracing";
import {
  isPlatformApiRequest,
  journeySessionHeaders,
} from "@zenith/utils/journeySession";
import { endPaygilantSession } from "@zenith/utils/paygilant";

const TokenHOC: React.FC = () => {
  const { navigateTo } = AppNavigation();
  const storeDecodedTokenContext = useUserDetailsContext();
  const { clearAllContexts } = useGlobalContextActions();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const isRefreshing = useRef(false);

  const authenticate = useCallback(async () => {
    if (isRefreshing.current) {
      return;
    }

    const refreshToken = sessionStorage.getItem(REFRESH) || undefined;
    const phoneNumber = sessionStorage.getItem("phoneNumber") || undefined;

    if (!refreshToken || !phoneNumber) {
      navigateTo(SIGN_IN);
      return;
    }

    isRefreshing.current = true;
    AuthenticationApiService.refreshToken(
      {
        phoneNumber,
        refreshToken,
      },
      "WEB"
    )
      .then((response) => {
        if (
          response?.data?.accessToken &&
          response.data.refreshToken &&
          response.data.expiresIn &&
          response.data.refreshTokenExpiresIn
        ) {
          handleTokenStore(
            ACCESS,
            response.data.accessToken,
            response.data.expiresIn
          );
          handleTokenStore(
            REFRESH,
            response.data.refreshToken,
            response.data.refreshTokenExpiresIn
          );
          setAccessToken(response.data.accessToken);
        }
      })
      .catch(() => {
        sessionStorage.setItem("registrationModalShown", "false");
        sessionStorage.removeItem(REFRESH);
        sessionStorage.removeItem(ACCESS);
        endPaygilantSession();
        clearAllContexts();
        // A full page load so the Paygilant SDK resets its session (see handleLogout).
        window.location.assign(SIGN_IN);
      })
      .finally(() => (isRefreshing.current = false));
  }, [clearAllContexts, navigateTo]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const isRefreshRequest =
          config.url?.includes("/refresh") ||
          config.url?.includes("refresh-token");

        if (!isRefreshRequest) {
          const token = sessionStorage.getItem(ACCESS);
          if (token) {
            decodeAndStoreToken(token, storeDecodedTokenContext);
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          // Explicitly remove Authorization for refresh requests
          delete config.headers.Authorization;
        }

        // Add tracing headers to every request
        const traceHeaders = generateTraceHeaders();
        config.headers["b3"] = traceHeaders.b3;
        config.headers["trace-id"] = traceHeaders["trace-id"];

        // Paygilant correlation key, only on calls to our own APIs
        if (isPlatformApiRequest(config.url)) {
          Object.entries(journeySessionHeaders()).forEach(([name, value]) => {
            config.headers[name] = value;
          });
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const traceId = originalRequest?.headers?.["trace-id"];

        // Handle token expiration by refreshing the token
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          if (!isRefreshing.current) {
            try {
              await authenticate();
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;

              // Preserve tracing headers for retry
              if (traceId) {
                const spanId = generateHexId(16);
                originalRequest.headers["b3"] = `${traceId}-${spanId}-1`;
                originalRequest.headers["trace-id"] = traceId;
              }

              return axios(originalRequest);
            } catch (refreshError) {
              return Promise.reject(refreshError);
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [accessToken, authenticate, storeDecodedTokenContext]);

  const { inactivityModal } = InactivityMethods();

  return (
    <>
      {inactivityModal}
      <Outlet />
    </>
  );
};

export default TokenHOC;
